import { GetSessionID, IsExistSessionID } from "../Utility/SessionUtility";

/**
 * ログインAPIに渡すパラメータの型
 */
export interface LoginParamType {
    loginId:string,
    password:string,
}

/**
 * ログインAPIから受け取るデータの型
 */
export interface LoginDataType {
    login_id:string,
    password:string,
    login_permitt:boolean,
    target_year:string,
    tempregi_num:number,
    artwork_num:number,
    session_id:string,
}

/**
 * ログインAPIを実行
 * @param param APIに渡すパラメータ
 * @returns LoginDataTypeのデータ、又はundefined
 */
export async function Login(param:LoginParamType) : Promise<LoginDataType | undefined> {

    const getAddress = `/competition/api/management/login?loginId=${param.loginId}&password=${param.password}`

    const result = await fetch(getAddress)
    .then(res => {
        return(res.ok ? res.json() : undefined); 
    })
    .catch(err => {
        console.error(err);
        return undefined;
    })

    return (result);
}

/**
 * 対象年度の事前登録情報をCSV形式で取得する
 * @param year 対象年度
 * @returns CSV形式の文字列、またはundefined
 */
export async function GetPreRegistrationCSV(year?:string) : Promise<string | undefined> {

    if(!IsExistSessionID()) {
        return(undefined);
    }

    const getAddress = `/competition/api/management/preregistration-csv/preregistration-${year}.csv?targetyear=${year}&sessionId=${GetSessionID()}`

    const result = await fetch(getAddress)
    .then(res => {
        return(res.ok ? res.text() : undefined); 
    })
    .catch(err => {
        console.error(err);
        return undefined;
    })

    return (result);
}

/**
 * 対象年度の本登録情報をCSV形式で取得する
 * @param year 対象年度
 * @returns CSV形式の文字列、またはundefined
 */
export async function GetWorkRegistrationCSV(year?:string) : Promise<string | undefined>  {

    if(!IsExistSessionID()) {
        return(undefined);
    }

    const getAddress = `/competition/api/management/registration-csv/registration-${year}.csv?targetyear=${year}&sessionId=${GetSessionID()}`

    const result = await fetch(getAddress)
    .then(res => {
        return(res.ok ? res.text() : undefined); 
    })
    .catch(err => {
        console.error(err);
        return undefined;
    })

    return (result);
}

/**
 * 作品画像アップロードAPIに渡すパラメータの型
 */
export interface WorkImageUploadType {
    artworkId?:string,
    imageFile?:File
}

/**
 * 作品画像アップロードAPIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostWorkImageUpload(params:WorkImageUploadType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/registrationArtworkDataUpd?artworkId=${params.artworkId}&sessionId=${GetSessionID()}`;

    const fd = new FormData();
    fd.append("artworkFile", typeof params.imageFile !== "undefined" ? params.imageFile : "");
        
    const result = await fetch(postAddress,{
        method:"POST",
        body:fd
    })
    .then(res => {
        return res.ok ? res.json() : undefined;
    })
    .then(res => {
        return res !== undefined ? res.result : false;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;

}

/**
 * 対象年度の作品データをダウンロードする
 * @param year 対象年度
 */
export function DownloadWorkFile(year:string) {
    const link = `/competition/api/management/artworkDataDownload?targetyear=${year}&sessionId=${GetSessionID()}`;
    window.location.href = link;
}

/**
 * 対象年度の在籍証明データをダウンロードする
 * @param year 対象年度
 */
export function DownloadCertificateFile(year:string) {
    const link = `/competition/api/management/proofFileDownload?targetyear=${year}&sessionId=${GetSessionID()}`;
    window.location.href = link;
}

/**
 * 年度変更APIを実行
 * @param year 対象年度
 * @returns true or false 
 */
export async function PostChangeTargetYear(year?:string) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/targetyear?targetYear=${year}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress,{
        method:"POST"
    })
    .then(res => {
        return res.ok
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 開催回変更APIに渡すパラメータの型
 */
interface ChangeHeldTimesType {
    heldTimesJP:string,
    heldTimesEN:string,
}

/**
 * 開催回変更APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostChangeHeldTimes(params:ChangeHeldTimesType) {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/heldTimesUpdate?heldTimesJp=${params.heldTimesJP}&heldTimesEn=${params.heldTimesEN}`

    const result = await fetch(postAddress,{
        method:"POST"
    })
    .then(res => {
        return res.ok
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;

}

/**
 * 受付期間変更APIに渡すパラメータの型
 */
export interface ChangePeriodType {
    advanceStartPeriod:string,
    advanceEndPeriod:string,
    registerStartPeriod:string,
    registerEndPeriod:string,
}

/**
 * 受付期間変更APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostChangePeriod(params:ChangePeriodType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/registerperiodSet?` +
                        `advanceStartPeriod=${params.advanceStartPeriod}` +
                        `&advanceEndPeriod=${params.advanceEndPeriod}` +
                        `&registerStartPeriod=${params.registerStartPeriod}` +
                        `&registerEndPeriod=${params.registerEndPeriod}` +
                        `&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress,{
        method:"POST"
    })
    .then(res => {
        return res.ok
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 対象年度の国マスタをCSV形式で取得する
 * @param year 対象年度
 * @returns CSV形式の文字列、またはundefined
 */
export async function GetCountryMasterCSV(year?:string) : Promise<string | undefined> {

    if(!IsExistSessionID()) {
        return(undefined);
    }

    const getAddress = `/competition/api/management/countrymaster-csv/country-${year}.csv?targetyear=${year}&sessionId=${GetSessionID()}`

    const result = await fetch(getAddress)
    .then(res => {
        return(res.ok ? res.text() : undefined); 
    })
    .catch(err => {
        console.error(err);
        return undefined;
    })

    return (result);
}

/**
 * 国マスタアップロードAPIに渡すパラメータの型
 */
export interface CountryMasterUploadType {
    year?:string,
    file?:File,
}

/**
 * 国マスタアップロードAPIを実行
 * 指定した年度の全ての国情報をアップロードする
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostCountryMasterUploadCSV(params:CountryMasterUploadType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    let sessionId = GetSessionID();

    const postAddress = `/competition/api/management/countrymaster-upload`;

    const fd = new FormData();
    fd.append("targetYear", typeof params.year !== "undefined" ? params.year : "");
    fd.append("upload_file", typeof params.file !== "undefined" ? params.file : "");
    fd.append("sessionId", sessionId ? sessionId : "");
        
    const result = await fetch(postAddress,{
        method:"POST",
        body:fd
    })
    .then(res => {
        return res.ok
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;

}

/**
 * 国マスタ更新APIに渡すパラメータの型
 */
export interface UpdateCountryType {
    countryId?: number,
    countryName: string,
    countryNameEn: string,
    regionId: number,
    countryCode2: string,
    countryCode3: string,
    dropdownDisp: string,
    targetYear?: string,
}

/**
 * 国マスタ更新APIを実行。
 * 指定した国IDのデータの内容を更新する
 * @param data APIに渡すパラメータ
 * @returns true or false
 */
export async function PostUpdateCountry(data:UpdateCountryType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/countryDataUpdate`

    const fd = new FormData();

    fd.append("data", JSON.stringify({
        countryId: data.countryId,
        countryName: data.countryName,
        countryNameEn: data.countryNameEn,
        regionId: data.regionId,
        countryCode2: data.countryCode2,
        countryCode3: data.countryCode3,
        dropdownDisp: data.dropdownDisp,
        targetYear: data.targetYear,
        sessionId: GetSessionID(),
    }))

    const result = await fetch(postAddress,{
        method:"POST",
        body:fd
    })
    .then(res => {
        return res.ok
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 指定した国IDのデータをDBから削除する
 * @param deleteId 削除対象の国ID
 * @returns true or false
 */
export async function PostDeleteCountry(deleteId:number) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/countryDataDelete?countryId=${deleteId}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress,{
        method:"POST",
    })
    .then(res => {
        return res.ok
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 地域追加APIに渡すパラメータの型
 */
export interface AddRegionType {
    regionId: number,
    regionName: string,
}

/**
 * 地域追加APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostAddRegion(params:AddRegionType) {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc030/add?` + 
                        `regionId=${params.regionId}&regionName=${params.regionName}&sessionId=${GetSessionID()}`;

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 地域削除APIを実行
 * @param regionNo 削除対象の地域番号
 * @returns true or false
 */
export async function PostDeleteRegion(regionNo:number) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc030/delete?regionNo=${regionNo}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 地域情報更新APIに渡すパラメータの型
 */
export interface UpdateRegionType {
    regionNo: number,
    regionName: string,
}

/**
 * 地域情報更新APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostUpdateRegion(params:UpdateRegionType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc030/update?` + 
                        `regionNo=${params.regionNo}&regionName=${params.regionName}&sessionId=${GetSessionID()}`;

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * ユーザー追加APIに渡すパラメータの型
 */
export interface AddUserType {
    loginId: string,
    userName: string,
    authorityId: number,
    password: string,
}

/**
 * ユーザー追加APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostAddUser(params:AddUserType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc010/add?` + 
                        `loginId=${params.loginId}&userName=${params.userName}&authorityId=${params.authorityId}` + 
                        `&password=${params.password}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * ユーザー削除APIを実行
 * @param userId 削除対象のユーザーID
 * @returns true or false
 */
export async function PostDeleteUser(userId?:number) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc010/delete?userId=${userId}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * ユーザー情報更新APIに渡すパラメータの型
 */
export interface UpdateUserType {
    userId: number,
    loginId?: string,
    userName: string,
    authorityId: number,
    password: string,
}

/**
 * ユーザー情報更新APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostUpdateUser(params:UpdateUserType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc010/update?` + 
                        `userId=${params.userId}&loginId=${params.loginId}&userName=${params.userName}&authorityId=${params.authorityId}` + 
                        `&password=${params.password}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 作品選択状況解除APIを実行
 * @param userId 対象のユーザーID
 * @returns true or false
 */
export async function PostReleaseUser(userId:number) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc010/selectrelease?userId=${userId}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}

/**
 * 権限情報更新APIに渡すパラメータの型
 */
interface UpdateAuthorityType {
    authorityId:number,
    authorityName:string,
    selectNum:number,
}

/**
 * 権限情報更新APIを実行
 * @param params APIに渡すパラメータ
 * @returns true or false
 */
export async function PostUpdateAuthority(params:UpdateAuthorityType) : Promise<boolean> {

    if(!IsExistSessionID()) {
        return(false);
    }

    const postAddress = `/competition/api/management/tmc020/update?` + 
                        `authorityId=${params.authorityId}&authorityName=${params.authorityName}&selectNum=${params.selectNum}&sessionId=${GetSessionID()}`

    const result = await fetch(postAddress, {
        method:"POST",
    })
    .then(res => {
        return res.ok ? res.json() : {result:false}
    })
    .then(data => {
        return data.result;
    })
    .catch(err => {
        console.error(err);
        return false;
    })

    return result;
}